import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Building2, Image as ImageIcon } from 'lucide-react';
import { api } from '@/api/client';

type AdminProfile = {
  companyName?: string;
  brandName?: string;
  logoUrl?: string;
  tagline?: string;
  supportEmail?: string;
  supportPhone?: string;
  website?: string;
  address?: string;
};

export function SuperAdminProfilePanel() {
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminProfile>({});

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'super_admin_profile'],
    queryFn: async () => {
      try { return (await api.get('/settings/super_admin_profile')).data as AdminProfile; }
      catch { return {} as AdminProfile; }
    },
  });

  useEffect(() => {
    if (data) setForm(data as AdminProfile);
  }, [data]);

  const save = useMutation({
    mutationFn: async (next: AdminProfile) => (await api.put('/settings/super_admin_profile', next)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'super_admin_profile'] }); toast.success('Profile saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const onLogo = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 1_200_000) { toast.error('Logo too large (max 1.2 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logoUrl: reader.result as string });
    reader.readAsDataURL(file);
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Building2 size={18} /> Super Admin Profile</h2>
        <p className="text-sm text-slate-500 mt-0.5">Your platform-level identity. The brand name and logo appear in the navbar; the company name flows into PDFs as the parent brand when no per-GSTIN profile is selected.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company Name (legal)">
            <input value={form.companyName ?? ''} onChange={(e) => setForm({ ...form, companyName: e.target.value })} placeholder="Gaint Logistic Private Limited" className="input" />
          </Field>
          <Field label="Brand Name (shown in UI)">
            <input value={form.brandName ?? ''} onChange={(e) => setForm({ ...form, brandName: e.target.value })} placeholder="Gaint" className="input" />
          </Field>
          <Field label="Tagline (optional)">
            <input value={form.tagline ?? ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Smarter Logistics for Modern Brands" className="input" />
          </Field>
          <Field label="Website">
            <input value={form.website ?? ''} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://gaintlogistic.com" className="input" />
          </Field>
          <Field label="Support Email">
            <input value={form.supportEmail ?? ''} onChange={(e) => setForm({ ...form, supportEmail: e.target.value })} placeholder="support@gaintlogistic.com" className="input" />
          </Field>
          <Field label="Support Phone">
            <input value={form.supportPhone ?? ''} onChange={(e) => setForm({ ...form, supportPhone: e.target.value })} placeholder="+91 ..." className="input" />
          </Field>
        </div>
        <Field label="Registered Address">
          <textarea value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3} className="input" placeholder="A-190, Sector-69, Noida-201301, UP" />
        </Field>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">Company Logo</label>
          <div className="flex items-start gap-5">
            <div className="border border-dashed border-slate-300 rounded-lg p-4 w-44 h-32 flex items-center justify-center bg-slate-50">
              {form.logoUrl ? (
                <img src={form.logoUrl} alt="logo" style={{ maxHeight: 100, maxWidth: 160 }} />
              ) : (
                <ImageIcon size={32} className="text-slate-300" />
              )}
            </div>
            <div className="flex-1">
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => onLogo(e.target.files?.[0] ?? undefined)} className="text-sm" />
              {form.logoUrl && (
                <button type="button" onClick={() => setForm({ ...form, logoUrl: '' })} className="block mt-2 text-xs text-danger hover:underline">Remove logo</button>
              )}
              <p className="text-xs text-slate-400 mt-2">PNG / JPG / SVG. Recommended 600 × 200 (header) or 220 × 220 (square). Max 1.2 MB.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => save.mutate(form)}
          disabled={save.isPending}
          className="px-5 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
